// src/pages/billing/Upgrade.jsx
// ---------------------------------------------------------------------
// Phase 1 billing page (per docs/pricing-strategy.md):
//   Do NOT build self-serve Stripe checkout, plan gates, usage meters,
//   or tier comparison tables until we've validated pricing with 10
//   manually-billed clubs. For the first 90 days we pitch the Founding
//   Club offer (Growth tier @ $199/mo locked for life, manual billing,
//   direct founder contact). When Phase 3 rolls around, restore the
//   previous self-serve tier grid — it's in git history.
// ---------------------------------------------------------------------
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  SparklesIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const FOUNDER_EMAIL = 'hello@getscrimly.com';
const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function Upgrade() {
  const { t } = useTranslation();

  const included = [
    t('upgrade.included.fullFeatures'),
    t('upgrade.included.paymentsAtCost'),
    t('upgrade.included.freeOnboarding'),
    t('upgrade.included.founderAccess'),
    t('upgrade.included.roadmap'),
    t('upgrade.included.priceLock'),
  ];

  const ask = [
    t('upgrade.ask.commitment'),
    t('upgrade.ask.caseStudy'),
    t('upgrade.ask.referrals'),
    t('upgrade.ask.feedback'),
    t('upgrade.ask.testimonial'),
  ];

  const mailto = `mailto:${FOUNDER_EMAIL}?subject=${encodeURIComponent(
    t('upgrade.emailSubject')
  )}&body=${encodeURIComponent(t('upgrade.emailBody'))}`;

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20">

        {/* ---- Header ---- */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#3d6010] mb-6"
            style={{ fontFamily: HEAD_FONT }}
          >
            <SparklesIcon className="h-3.5 w-3.5" />
            {t('upgrade.badge')}
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-[#0d1f14] mb-4 leading-tight"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('upgrade.headingLine1')}<br />
            <span className="text-[#5a8a1f]">{t('upgrade.headingLine2')}</span>
          </h1>
          <p className="text-lg text-zinc-500 max-w-xl mx-auto">
            {t('upgrade.subheading')}
          </p>
        </div>

        {/* ---- Offer card ---- */}
        <div className="relative rounded-3xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-7 sm:p-10 mb-8">
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-[#0d1f14] px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-[#b5f03d] whitespace-nowrap shadow-lg"
            style={{ fontFamily: HEAD_FONT }}
          >
            <SparklesIcon className="h-3 w-3" />
            {t('upgrade.firstFive')}
          </div>

          <div className="flex items-baseline gap-2 mb-2">
            <span
              className="text-5xl font-black tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              $199
            </span>
            <span className="text-sm text-zinc-400 font-medium">{t('upgrade.priceMonth')}</span>
          </div>
          <p className="text-sm text-[#5a8a1f] font-bold mb-8">
            {t('upgrade.priceLocked')}
          </p>

          <div className="grid sm:grid-cols-2 gap-8">
            <div>
              <h3
                className="text-xs font-bold uppercase tracking-widest text-[#5a8a1f] mb-3"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('upgrade.whatYouGet')}
              </h3>
              <ul className="space-y-2.5">
                {included.map((line) => (
                  <li key={line} className="flex gap-2 text-sm text-zinc-700">
                    <CheckIcon className="h-5 w-5 shrink-0 text-[#94cc28]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3
                className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('upgrade.whatWeAsk')}
              </h3>
              <ul className="space-y-2.5">
                {ask.map((line) => (
                  <li key={line} className="flex gap-2 text-sm text-zinc-700">
                    <CheckIcon className="h-5 w-5 shrink-0 text-[#0d1f14]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mailto}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all"
              style={{ fontFamily: HEAD_FONT }}
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              {t('upgrade.emailCta')}
            </a>
            <span className="text-xs text-zinc-400">
              {t('upgrade.orReachUs')}{' '}
              <a href={mailto} className="font-semibold text-[#3d6010] hover:underline">
                {FOUNDER_EMAIL}
              </a>
            </span>
          </div>
        </div>

        {/* ---- Why-us note ---- */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-sm text-zinc-600 leading-relaxed">
          <p
            className="mb-2 font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('upgrade.whyTitle')}
          </p>
          <p>{t('upgrade.whyBody')}</p>
        </div>

        <p className="mt-8 text-center text-xs text-zinc-400">
          {t('upgrade.footer')}
        </p>
      </div>
    </div>
  );
}
